import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MdOutlineIntegrationInstructions } from 'react-icons/md';
import { HiOutlineCog, HiOutlineTrash, HiOutlinePlus, HiOutlinePaperAirplane, HiOutlineAdjustments, HiOutlineExclamationCircle, HiOutlineRefresh, HiOutlineEye, HiOutlineCode, HiOutlineArchive } from 'react-icons/hi';
import { API } from '../redux/api/utils';
import { useToast } from '../components/shared/ToastContainer';
import useHiddenAction from '../hooks/useHiddenAction';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import TextInput from '../components/shared/TextInput';
import Modal from '../components/shared/Modal';
import Badge from '../components/shared/Badge';
import { useIsAdmin } from '../hooks/useIsAdmin';
import * as XLSX from 'xlsx';

const STATUS_BADGE_VARIANTS = {
    succeeded: 'success',
    failed: 'danger',
    pending: 'warning',
    retrying: 'cream'
};

const Integrations = () => {
    const toast = useToast();
    const isAdmin = useIsAdmin();
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
    const [showPropertiesModal, setShowPropertiesModal] = useState(false);
    const [selectedProperties, setSelectedProperties] = useState(null);
    const [showApiResponseModal, setShowApiResponseModal] = useState(false);
    const [apiResponseData, setApiResponseData] = useState(null);
    const [apiResponseLoading, setApiResponseLoading] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
    const [toggleTarget, setToggleTarget] = useState(null);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        succeeded: 0,
        failed: 0,
        pending: 0,
        retrying: 0
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSubmissions, setTotalSubmissions] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [submitData, setSubmitData] = useState({
        identification: '',
        contractNumber: ''
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [testLoading, setTestLoading] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [testContractNumber, setTestContractNumber] = useState('');
    const [rulesData, setRulesData] = useState({
        apiUrl: '',
        credentials: {
            username: '',
            password: '',
            apiKey: '',
            headers: {}
        },
        rules: {
            parentName: '',
            itemCodeField: '',
            totalLengthField: '',
            rewardsPercentage: 0,
            additionalFields: []
        }
    });

    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);

    // Hidden action: press the "X" key 15 times while submissions modal is open to unlock retry on all submissions
    const { isUnlocked: retrySucceededUnlocked, reset: resetRetrySucceeded } = useHiddenAction({ key: 'x', presses: 15, active: showSubmissionsModal });

    useEffect(() => {
        document.title = "Integrations | LoyalBase";
        if (currentOrganisation?._id) {
            fetchIntegrations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrganisation]);

    const fetchIntegrations = async () => {
        try {
            const response = await API.get(`/integrations/fetch-integrations?organisationId=${currentOrganisation._id}`);
            setIntegrations(response.data.integrations || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching integrations:', error);
            setLoading(false);
        }
    };

    const handleCreateIntegration = () => {
        setFormData({ name: '', description: '' });
        setSelectedIntegration(null);
        setShowCreateModal(true);
    };

    const handleEditIntegration = (integration) => {
        setSelectedIntegration(integration);
        setFormData({
            name: integration.name,
            description: integration.description || ''
        });
        setShowEditModal(true);
    };

    const handleArchiveIntegration = async (integrationId) => {
        if (window.confirm('Are you sure you want to archive this integration? It will be hidden from your list but not deleted.')) {
            try {
                await API.post('/integrations/archive-integration', {
                    organisationId: currentOrganisation._id,
                    integrationId: integrationId
                });
                toast.success('Integration archived successfully');
                setIntegrations(prev => prev.filter(int => int._id !== integrationId));
            } catch (error) {
                console.error('Error archiving integration:', error);
                toast.error('Error archiving integration');
            }
        }
    };

    const handleSaveIntegration = async () => {
        if (!formData.name.trim()) {
            toast.warning('Please enter an integration name');
            return;
        }

        try {
            if (selectedIntegration) {
                await API.post('/integrations/update-integration', {
                    organisationId: currentOrganisation._id,
                    integration: {
                        _id: selectedIntegration._id,
                        name: formData.name,
                        description: formData.description
                    }
                });
                toast.success('Integration updated successfully');
            } else {
                await API.post('/integrations/create-integration', {
                    organisationId: currentOrganisation._id,
                    integration: {
                        name: formData.name,
                        description: formData.description
                    }
                });
                toast.success('Integration created successfully');
            }

            fetchIntegrations();
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedIntegration(null);
            setFormData({ name: '', description: '' });
        } catch (error) {
            console.error('Error saving integration:', error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Error saving integration. Please check the console for details.');
            }
        }
    };

    const handleToggleIntegration = (integration) => {
        setToggleTarget(integration);
        setShowToggleConfirmModal(true);
    };

    const confirmToggleIntegration = async () => {
        if (!toggleTarget) return;
        setToggleLoading(true);
        try {
            const newStatus = toggleTarget.status === 'active' ? false : true;
            await API.post('/integrations/toggle-integration', {
                organisationId: currentOrganisation._id,
                integrationId: toggleTarget._id,
                isEnabled: newStatus
            });
            await fetchIntegrations();
            setShowToggleConfirmModal(false);
            setToggleTarget(null);
        } catch (error) {
            console.error('Error toggling integration:', error);
            toast.error('Error updating integration status');
        } finally {
            setToggleLoading(false);
        }
    };

    const handleSubmitIntegration = (integration) => {
        setSelectedIntegration(integration);
        setSubmitData({ identification: '', contractNumber: '' });
        setSubmitMessage('');
        setShowSubmitModal(true);
    };

    const handleManageRules = (integration) => {
        setSelectedIntegration(integration);
        setRulesData({
            apiUrl: integration.config?.apiUrl || '',
            credentials: {
                username: integration.config?.credentials?.username || '',
                password: integration.config?.credentials?.password || '',
                apiKey: integration.config?.credentials?.apiKey || '',
                headers: integration.config?.credentials?.headers || {}
            },
            rules: {
                parentName: integration.config?.rules?.parentName || '',
                itemCodeField: integration.config?.rules?.itemCodeField || '',
                totalLengthField: integration.config?.rules?.totalLengthField || '',
                rewardsPercentage: integration.config?.rules?.rewardsPercentage || 0,
                additionalFields: integration.config?.rules?.additionalFields || []
            }
        });
        setShowRulesModal(true);
    };

    const handleAddAdditionalField = () => {
        setRulesData(prev => ({
            ...prev,
            rules: {
                ...prev.rules,
                additionalFields: [...prev.rules.additionalFields, {
                    fieldName: '',
                    fieldType: 'string',
                    defaultValue: ''
                }]
            }
        }));
    };

    const handleRemoveAdditionalField = (index) => {
        setRulesData(prev => ({
            ...prev,
            rules: {
                ...prev.rules,
                additionalFields: prev.rules.additionalFields.filter((_, i) => i !== index)
            }
        }));
    };

    const handleAdditionalFieldChange = (index, field, value) => {
        setRulesData(prev => ({
            ...prev,
            rules: {
                ...prev.rules,
                additionalFields: prev.rules.additionalFields.map((af, i) =>
                    i === index ? { ...af, [field]: value } : af
                )
            }
        }));
    };

    const handleTestRules = async () => {
        if (!testContractNumber.trim()) {
            toast.warning('Please enter a contract number to test with');
            return;
        }

        if (!rulesData.apiUrl.trim()) {
            toast.warning('Please enter an API URL to test');
            return;
        }

        setTestLoading(true);
        setTestResults(null);

        try {
            const response = await API.post('/integrations/test-integration-rules', {
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                rulesConfig: rulesData,
                testContractNumber: testContractNumber
            });

            setTestResults(response.data);
            toast.success('Test completed successfully');
        } catch (error) {
            console.error('Error testing rules:', error);
            setTestResults({
                success: false,
                error: error.response?.data?.message || 'Failed to test API connection'
            });
            toast.error('Failed to test API connection');
        } finally {
            setTestLoading(false);
        }
    };

    const handleSaveRules = async () => {
        try {
            await API.post('/integrations/update-integration-rules', {
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                rulesConfig: rulesData
            });

            fetchIntegrations();
            setShowRulesModal(false);
            setSelectedIntegration(null);
            setTestResults(null);
            setTestContractNumber('');
            toast.success('Rules saved successfully');
        } catch (error) {
            console.error('Error saving rules:', error);
            toast.error('Error saving rules. Please try again.');
        }
    };

    const handleSubmitSubmission = async () => {
        if (!submitData.identification.trim()) {
            setSubmitMessage('Please enter an identification');
            return;
        }

        if (!submitData.contractNumber.trim()) {
            setSubmitMessage('Please enter a contract number');
            return;
        }

        setSubmitLoading(true);
        setSubmitMessage('');

        try {
            await API.post('/integrations/submit', {
                organisationId: currentOrganisation._id,
                submission: {
                    identification: submitData.identification,
                    integrationId: selectedIntegration._id,
                    contractNumber: submitData.contractNumber
                }
            });

            setSubmitMessage('Submission successful! Points have been awarded.');

            setTimeout(() => {
                setShowSubmitModal(false);
                setSubmitMessage('');
            }, 2000);

        } catch (error) {
            console.error('Error submitting integration:', error);
            const errorMessage = error.response?.data?.message || 'An error occurred while submitting';
            setSubmitMessage(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    const fetchAllSubmissionsForExport = async () => {
        if (!selectedIntegration || !currentOrganisation?._id) return [];
        const all = [];
        const exportPageSize = 200;
        let pageNum = 1;
        let totalPagesNum = 1;
        do {
            const params = new URLSearchParams({
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                status: statusFilter,
                sort: sortBy,
                page: String(pageNum),
                pageSize: String(exportPageSize),
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);
            const response = await API.get(`/integrations/submissions?${params.toString()}`);
            all.push(...(response.data.submissions || []));
            totalPagesNum = response.data.totalPages || 1;
            pageNum += 1;
        } while (pageNum <= totalPagesNum);
        return all;
    };

    const buildSubmissionRows = (submissions) => submissions.map(s => {
        const r = s.lastApiResponse || {};
        const stringify = (v) => {
            if (v == null) return '';
            if (typeof v === 'string') return v;
            try { return JSON.stringify(v); } catch { return String(v); }
        };
        return {
            Status: s.status,
            Member: s.identification || '',
            'Contract Number': s.contractNumber || '',
            'Submitted At': s.submittedAt ? new Date(s.submittedAt).toISOString() : '',
            Error: s.error || r.error || '',
            'API URL': r.url || '',
            'API Query': stringify(r.params),
            'API Response': stringify(r.responseBody),
        };
    });

    const [exportingSubmissions, setExportingSubmissions] = useState(false);

    const exportSubmissionsCSV = async (integration) => {
        try {
            setExportingSubmissions(true);
            const all = await fetchAllSubmissionsForExport();
            if (!all.length) { toast.warning('No submissions to export'); return; }
            const rows = buildSubmissionRows(all);
            const headers = Object.keys(rows[0]);
            const escape = (v) => {
                const str = v == null ? '' : String(v);
                return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
            };
            const csv = [
                headers.join(','),
                ...rows.map(r => headers.map(h => escape(r[h])).join(',')),
            ].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${integration?.name || 'integration'}_submissions.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            toast.error('Failed to export submissions');
        } finally {
            setExportingSubmissions(false);
        }
    };

    const exportSubmissionsXLSX = async (integration) => {
        try {
            setExportingSubmissions(true);
            const all = await fetchAllSubmissionsForExport();
            if (!all.length) { toast.warning('No submissions to export'); return; }
            const ws = XLSX.utils.json_to_sheet(buildSubmissionRows(all));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Submissions');
            XLSX.writeFile(wb, `${integration?.name || 'integration'}_submissions.xlsx`);
        } catch (e) {
            console.error(e);
            toast.error('Failed to export submissions');
        } finally {
            setExportingSubmissions(false);
        }
    };

    const handleViewSubmissions = (integration, initialStatus = 'all') => {
        resetRetrySucceeded();
        setSelectedIntegration(integration);
        setShowSubmissionsModal(true);
        setStatusFilter(initialStatus);
        setSearchQuery('');
        setDebouncedSearch('');
        setDateFrom('');
        setDateTo('');
        setSortBy('newest');
        setPage(1);
        setPageSize(25);
    };

    const handleFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
        setPage(1);
    };

    const fetchSubmissions = async () => {
        if (!selectedIntegration || !currentOrganisation?._id) return;
        setSubmissionsLoading(true);
        try {
            const params = new URLSearchParams({
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                status: statusFilter,
                sort: sortBy,
                page: String(page),
                pageSize: String(pageSize)
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);

            const response = await API.get(`/integrations/submissions?${params.toString()}`);
            setSubmissions(response.data.submissions || []);
            setStatusCounts(response.data.statusCounts || {
                all: 0,
                succeeded: 0,
                failed: 0,
                pending: 0,
                retrying: 0
            });
            setTotalSubmissions(response.data.total || 0);
            setTotalPages(response.data.totalPages || 1);
            if (response.data.page && response.data.page !== page) {
                setPage(response.data.page);
            }
        } catch (error) {
            console.error('Error fetching submissions:', error);
            toast.error('Error loading submissions');
        } finally {
            setSubmissionsLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        if (showSubmissionsModal && selectedIntegration) {
            fetchSubmissions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showSubmissionsModal,
        selectedIntegration?._id,
        statusFilter,
        debouncedSearch,
        dateFrom,
        dateTo,
        sortBy,
        page,
        pageSize
    ]);

    const handleRetrySubmissions = async (submissionIds = []) => {
        if (!selectedIntegration) return;

        setRetrying(true);

        try {
            const response = await API.post('/integrations/retry-submissions', {
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                submissionIds: submissionIds
            });

            toast.success(response.data.message);

            await fetchSubmissions();
            fetchIntegrations();
        } catch (error) {
            console.error('Error retrying submissions:', error);
            toast.error('Error retrying submissions');
        } finally {
            setRetrying(false);
        }
    };

    const handleDeleteSubmissions = async (submissionIds = []) => {
        if (!selectedIntegration) return;

        const confirmMessage = submissionIds.length > 0
            ? 'Are you sure you want to delete the selected submissions?'
            : 'Are you sure you want to delete ALL submissions?';

        if (!window.confirm(confirmMessage)) return;

        try {
            await API.post('/integrations/delete-submissions', {
                organisationId: currentOrganisation._id,
                integrationId: selectedIntegration._id,
                submissionIds: submissionIds
            });

            toast.success('Submissions deleted successfully');

            await fetchSubmissions();
            fetchIntegrations();
        } catch (error) {
            console.error('Error deleting submissions:', error);
            toast.error('Error deleting submissions');
        }
    };

    const handleViewProperties = (properties) => {
        setSelectedProperties(properties);
        setShowPropertiesModal(true);
    };

    const handleViewLastApiResponse = async (submission) => {
        setApiResponseLoading(true);
        setApiResponseData(null);
        setShowApiResponseModal(true);

        try {
            const response = await API.get(`/integrations/last-api-response?organisationId=${currentOrganisation._id}&integrationId=${selectedIntegration._id}&submissionId=${submission._id}`);
            setApiResponseData(response.data);
        } catch (error) {
            console.error('Error fetching last API response:', error);
            setApiResponseData({
                lastApiResponse: null,
                error: error.response?.data?.message || 'Failed to fetch last API response'
            });
        } finally {
            setApiResponseLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-canvas">
                <div className="text-center">
                    <MdOutlineIntegrationInstructions className="text-6xl mx-auto mb-4 text-muted" />
                    <p className="text-body-md text-muted">Loading integrations...</p>
                </div>
            </div>
        );
    }

    const renderIntegrationFormFields = () => (
        <div className="space-y-4">
            <TextInput
                label="Integration Name *"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter integration name"
            />
            <TextInput
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter integration description (optional)"
            />
        </div>
    );

    return (
        <div className="min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center w-12 h-12 rounded-md mr-4 bg-surface-soft border border-hairline">
                                <MdOutlineIntegrationInstructions className="text-2xl text-ink" />
                            </div>
                            <div>
                                <h1 className="text-display-md text-ink" data-help-title="Integrations" data-help="Connects LoyalBase to your other systems such as point-of-sale, e-commerce, ERP, or CRM. Each integration has its own rules for reading the data it receives, and you can monitor every submission it processes.">
                                    Custom Integrations
                                </h1>
                                <p className="text-body-md text-body mt-1">
                                    Create and manage your custom integrations
                                </p>
                            </div>
                        </div>
                        {isAdmin && (
                            <Button
                                variant="primary"
                                onClick={handleCreateIntegration}
                                leadingIcon={<HiOutlinePlus />}
                                data-help-title="Create integration"
                                data-help="Opens a form to create a new integration with a name and optional description. Each integration represents one external system that submits data to LoyalBase to award points. Admin only."
                            >
                                Create Integration
                            </Button>
                        )}
                    </div>
                </div>

                {/* Integrations List */}
                {integrations.length === 0 ? (
                    <div className="text-center py-16">
                        <MdOutlineIntegrationInstructions className="text-6xl mx-auto mb-4 text-muted" />
                        <h3 className="text-title-md text-ink mb-2">
                            No custom integrations yet
                        </h3>
                        <p className="text-body-md text-body mb-6">
                            Create your first custom integration to get started.
                        </p>
                        {isAdmin && (
                            <Button
                                variant="primary"
                                onClick={handleCreateIntegration}
                                leadingIcon={<HiOutlinePlus />}
                                data-help-title="Create integration"
                                data-help="Opens a form to create a new integration with a name and optional description. Each integration represents one external system that submits data to LoyalBase to award points. Admin only."
                            >
                                Create Integration
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {integrations.map(integration => (
                            <Card key={integration._id} variant="default">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div className="flex items-center">
                                        <div className="flex items-center justify-center w-12 h-12 rounded-md mr-4 bg-surface-soft border border-hairline">
                                            <MdOutlineIntegrationInstructions className="text-2xl text-ink" />
                                        </div>
                                        <div>
                                            <h3 className="text-title-sm text-ink">
                                                {integration.name}
                                            </h3>
                                            <p className="text-body-md text-body">
                                                {integration.description || 'No description provided'}
                                            </p>
                                            <div className="flex items-center mt-1 space-x-2">
                                                <p className="text-body-md text-muted">ID:</p>
                                                <code
                                                    data-help-title="Integration ID"
                                                    data-help="Shows this integration's unique ID. Clicking it copies the ID to the clipboard for use in API calls that target this integration."
                                                    className="text-body-md px-2 py-1 rounded-sm bg-surface-soft text-ink cursor-pointer font-mono hover:bg-surface-strong transition-colors"
                                                    onClick={(event) => {
                                                        navigator.clipboard.writeText(integration._id);
                                                        const originalText = event.target.textContent;
                                                        event.target.textContent = 'Copied!';
                                                        setTimeout(() => {
                                                            event.target.textContent = originalText;
                                                        }, 1000);
                                                    }}
                                                    title="Click to copy ID"
                                                >
                                                    {integration._id}
                                                </code>
                                            </div>
                                            <p className="text-body-md text-muted mt-1">
                                                Created on {new Date(integration.connectedAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 flex-wrap">
                                        {integration.submissions && integration.submissions.length > 0 && (
                                            <button
                                                onClick={() => handleViewSubmissions(integration, 'all')}
                                                className="inline-flex items-center gap-1"
                                                title="View all submissions"
                                                data-help-title="All submissions"
                                                data-help="Opens the submissions browser showing every submission this integration has received."
                                            >
                                                <Badge variant="default">
                                                    <HiOutlineExclamationCircle className="mr-1" />
                                                    {integration.submissions.length} Submissions
                                                </Badge>
                                            </button>
                                        )}
                                        {integration.submissions && integration.submissions.filter(s => s.status === 'failed').length > 0 && (
                                            <button
                                                onClick={() => handleViewSubmissions(integration, 'failed')}
                                                className="inline-flex items-center gap-1"
                                                title="View failed submissions"
                                                data-help-title="Failed submissions"
                                                data-help="Opens the submissions browser filtered to only this integration's failed submissions so you can inspect and retry them."
                                            >
                                                <Badge variant="warning">
                                                    <HiOutlineExclamationCircle className="mr-1" />
                                                    {integration.submissions.filter(s => s.status === 'failed').length} Failed
                                                </Badge>
                                            </button>
                                        )}

                                        {isAdmin ? (
                                            <button
                                                onClick={() => handleToggleIntegration(integration)}
                                                title={`Click to ${integration.status === 'active' ? 'disable' : 'enable'}`}
                                                data-help-title="Status toggle"
                                                data-help="Shows whether the integration is processing submissions. Clicking opens a confirmation to activate or deactivate it; while inactive, new submissions are not processed and pending ones are not retried. Admin only."
                                            >
                                                <Badge variant={integration.status === 'active' ? 'success' : 'danger'}>
                                                    {integration.status === 'active' ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </button>
                                        ) : (
                                            <Badge variant={integration.status === 'active' ? 'success' : 'danger'} data-help-title="Integration status" data-help="Shows whether the integration is currently processing submissions.">
                                                {integration.status === 'active' ? 'Active' : 'Inactive'}
                                            </Badge>
                                        )}

                                        {isAdmin && integration.status === 'active' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleSubmitIntegration(integration)}
                                                title="Submit Integration Data"
                                                data-help-title="Submit data"
                                                data-help="Opens a form to manually send a submission with a member identification and contract number, awarding points as if the data came from the external system. Useful for confirming the integration is wired up correctly. Admin only."
                                            >
                                                <HiOutlinePaperAirplane className="text-lg" />
                                            </Button>
                                        )}

                                        {isAdmin && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleManageRules(integration)}
                                                    title="Manage Rules"
                                                    data-help-title="Manage rules"
                                                    data-help="Opens the rules editor where you set the API URL, credentials, and field mapping that tell LoyalBase how to read this integration's incoming data. Admin only."
                                                >
                                                    <HiOutlineAdjustments className="text-lg" />
                                                </Button>

                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleEditIntegration(integration)}
                                                    title="Edit"
                                                    data-help-title="Edit integration"
                                                    data-help="Opens a form to change this integration's name and description. Admin only."
                                                >
                                                    <HiOutlineCog className="text-lg" />
                                                </Button>

                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleArchiveIntegration(integration._id)}
                                                    title="Archive"
                                                    data-help-title="Archive integration"
                                                    data-help="Archives this integration after confirmation. It is hidden from the list but its historic data remains intact; nothing is permanently deleted. Admin only."
                                                >
                                                    <HiOutlineArchive className="text-lg" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Toggle Status Confirmation Modal */}
            <Modal
                isOpen={showToggleConfirmModal && !!toggleTarget}
                onClose={() => {
                    if (toggleLoading) return;
                    setShowToggleConfirmModal(false);
                    setToggleTarget(null);
                }}
                title={toggleTarget?.status === 'active' ? 'Deactivate integration?' : 'Activate integration?'}
                size="sm"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowToggleConfirmModal(false);
                                setToggleTarget(null);
                            }}
                            disabled={toggleLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={toggleTarget?.status === 'active' ? 'danger' : 'primary'}
                            onClick={confirmToggleIntegration}
                            disabled={toggleLoading}
                        >
                            {toggleLoading
                                ? 'Saving...'
                                : toggleTarget?.status === 'active'
                                    ? 'Deactivate'
                                    : 'Activate'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-body-md text-body">
                        {toggleTarget?.status === 'active' ? (
                            <>You're about to deactivate <span className="font-medium text-ink">{toggleTarget?.name}</span>. New submissions will stop being processed until it's activated again.</>
                        ) : (
                            <>You're about to activate <span className="font-medium text-ink">{toggleTarget?.name}</span>. It will start accepting and processing submissions.</>
                        )}
                    </p>
                    {toggleTarget?.status === 'active' && (
                        <div className="p-3 rounded-sm border border-signature-coral bg-signature-coral/10">
                            <p className="text-body-md text-signature-coral">
                                Existing pending submissions will not be retried while the integration is inactive.
                            </p>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Create Integration Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create Custom Integration"
                size="md"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSaveIntegration}>Create Integration</Button>
                    </>
                }
            >
                {renderIntegrationFormFields()}
            </Modal>

            {/* Edit Integration Modal */}
            <Modal
                isOpen={showEditModal && !!selectedIntegration}
                onClose={() => setShowEditModal(false)}
                title="Edit Integration"
                size="md"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSaveIntegration}>Update Integration</Button>
                    </>
                }
            >
                {renderIntegrationFormFields()}
            </Modal>

            {/* Submit Integration Modal */}
            <Modal
                isOpen={showSubmitModal && !!selectedIntegration}
                onClose={() => setShowSubmitModal(false)}
                title="Submit Integration Data"
                size="md"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowSubmitModal(false)} disabled={submitLoading}>Cancel</Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmitSubmission}
                            disabled={submitLoading}
                            leadingIcon={!submitLoading ? <HiOutlinePaperAirplane /> : null}
                        >
                            {submitLoading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </>
                }
            >
                <div className="mb-4">
                    <p className="text-body-md text-body">
                        Integration: <span className="font-medium text-ink">{selectedIntegration?.name}</span>
                    </p>
                </div>
                <div className="space-y-4">
                    <TextInput
                        label="Member Identification *"
                        type="text"
                        value={submitData.identification}
                        onChange={(e) => setSubmitData(prev => ({ ...prev, identification: e.target.value }))}
                        placeholder="Enter member identification (email, ID, etc.)"
                        disabled={submitLoading}
                    />
                    <TextInput
                        label="Contract Number *"
                        type="text"
                        value={submitData.contractNumber}
                        onChange={(e) => setSubmitData(prev => ({ ...prev, contractNumber: e.target.value }))}
                        placeholder="Enter contract number"
                        disabled={submitLoading}
                    />
                </div>
                {submitMessage && (
                    <div className={`mt-4 p-3 rounded-sm text-body-md border ${
                        submitMessage.includes('successful')
                            ? 'bg-signature-mint border-hairline text-signature-forest'
                            : 'bg-signature-coral/10 border-signature-coral text-signature-coral'
                    }`}>
                        {submitMessage}
                    </div>
                )}
            </Modal>

            {/* Rules Management Modal */}
            <Modal
                isOpen={showRulesModal && !!selectedIntegration}
                onClose={() => {
                    setShowRulesModal(false);
                    setTestResults(null);
                    setTestContractNumber('');
                }}
                title={selectedIntegration ? `Manage Rules - ${selectedIntegration.name}` : 'Manage Rules'}
                size="lg"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => {
                            setShowRulesModal(false);
                            setTestResults(null);
                            setTestContractNumber('');
                        }}>Cancel</Button>
                        <Button variant="primary" onClick={handleSaveRules} data-help-title="Save rules" data-help="Saves the API configuration, field mapping, additional fields, and rewards percentage for this integration.">Save Rules</Button>
                    </>
                }
            >
                <div className="space-y-6">
                    {/* Test API Section */}
                    {rulesData.apiUrl && (
                        <div className="border border-hairline rounded-md p-4 bg-surface-soft">
                            <h4 className="text-title-sm text-ink mb-4">Test API Configuration</h4>
                            <div className="flex items-end gap-3 mb-4">
                                <div className="flex-1">
                                    <TextInput
                                        label="Test Contract Number"
                                        type="text"
                                        value={testContractNumber}
                                        onChange={(e) => setTestContractNumber(e.target.value)}
                                        placeholder="Enter a contract number to test with"
                                        disabled={testLoading}
                                        data-help-title="Test contract number"
                                        data-help="Holds a sample contract number that the Test API button sends to the configured API, so you can validate the setup before saving."
                                    />
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleTestRules}
                                    disabled={testLoading || !testContractNumber.trim() || !rulesData.apiUrl.trim()}
                                    data-help-title="Test API"
                                    data-help="Runs a live test call to the configured API URL with the test contract number and shows the calculated points, applied multiplier, matched rule, and raw API response. Lets you validate the configuration before saving."
                                >
                                    {testLoading ? 'Testing...' : 'Test API'}
                                </Button>
                            </div>

                            {testResults && (
                                <div className={`p-4 rounded-sm border ${
                                    testResults.success
                                        ? 'bg-signature-mint border-hairline text-signature-forest'
                                        : 'bg-signature-coral/10 border-signature-coral text-signature-coral'
                                }`}>
                                    {testResults.success ? (
                                        <div>
                                            <h5 className="font-medium mb-2">Test Successful</h5>
                                            <div className="text-body-md space-y-1">
                                                <p><strong>API Response:</strong> {testResults.apiCallSuccessful ? 'Success' : 'Failed'}</p>
                                                {!testResults.apiCallSuccessful && testResults.apiError && (
                                                    <div className="ml-4 text-xs bg-canvas border border-hairline p-2 rounded-sm text-ink">
                                                        <p><strong>Error:</strong> {testResults.apiError.message}</p>
                                                        {testResults.apiError.status && (
                                                            <p><strong>Status:</strong> {testResults.apiError.status} {testResults.apiError.statusText}</p>
                                                        )}
                                                    </div>
                                                )}
                                                <p><strong>Base Value:</strong> {testResults.baseValue || 'N/A'}</p>
                                                <p><strong>System Value:</strong> {testResults.systemValue || 'N/A'}</p>
                                                <p><strong>Color Value:</strong> {testResults.colorValue || 'N/A'}</p>
                                                <p><strong>Applied Multiplier:</strong> {testResults.appliedMultiplier || 'N/A'}</p>
                                                <p><strong>Calculated Points:</strong> {testResults.calculatedPoints}</p>
                                                {testResults.matchingRule && (
                                                    <p><strong>Matching Rule:</strong> System {testResults.matchingRule.systemValue}
                                                        {testResults.matchingRule.colorValue ? ` + Color ${testResults.matchingRule.colorValue}` : ''}
                                                        (×{testResults.matchingRule.multiplier})</p>
                                                )}
                                                {testResults.rawApiData && (
                                                    <details className="mt-2">
                                                        <summary className="cursor-pointer font-medium">Raw API Response</summary>
                                                        <pre className="mt-1 text-xs bg-canvas border border-hairline p-2 rounded-sm overflow-auto max-h-32 text-ink">
                                                            {JSON.stringify(testResults.rawApiData, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <h5 className="font-medium mb-2">Test Failed</h5>
                                            <p className="text-body-md">{testResults.error}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* API Configuration */}
                    <div className="border border-hairline rounded-md p-4">
                        <h4 className="text-title-sm text-ink mb-4">API Configuration</h4>
                        <div className="space-y-4">
                            <TextInput
                                label="API URL"
                                type="url"
                                value={rulesData.apiUrl}
                                onChange={(e) => setRulesData(prev => ({ ...prev, apiUrl: e.target.value }))}
                                placeholder="https://api.example.com/data"
                                data-help-title="API URL"
                                data-help="Sets the endpoint URL this integration calls to look up data for each submission."
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TextInput
                                    label="Username"
                                    type="text"
                                    value={rulesData.credentials.username}
                                    onChange={(e) => setRulesData(prev => ({
                                        ...prev,
                                        credentials: { ...prev.credentials, username: e.target.value }
                                    }))}
                                    placeholder="Optional"
                                    data-help-title="API username"
                                    data-help="Sets the optional username this integration uses to authenticate with the external API."
                                />
                                <TextInput
                                    label="Password"
                                    type="password"
                                    value={rulesData.credentials.password}
                                    onChange={(e) => setRulesData(prev => ({
                                        ...prev,
                                        credentials: { ...prev.credentials, password: e.target.value }
                                    }))}
                                    placeholder="Optional"
                                    data-help-title="API password"
                                    data-help="Sets the optional password this integration uses to authenticate with the external API."
                                />
                                <TextInput
                                    label="API Key"
                                    type="text"
                                    value={rulesData.credentials.apiKey}
                                    onChange={(e) => setRulesData(prev => ({
                                        ...prev,
                                        credentials: { ...prev.credentials, apiKey: e.target.value }
                                    }))}
                                    placeholder="Optional"
                                    data-help-title="API key"
                                    data-help="Sets the optional API key this integration sends when calling the external API."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Rules Configuration */}
                    <div className="border border-hairline rounded-md p-4">
                        <h4 className="text-title-sm text-ink mb-4">Field Configuration</h4>
                        <div className="space-y-4">
                            <TextInput
                                label="Parent Name (Optional)"
                                type="text"
                                value={rulesData.rules.parentName}
                                onChange={(e) => setRulesData(prev => ({
                                    ...prev,
                                    rules: { ...prev.rules, parentName: e.target.value }
                                }))}
                                placeholder="e.g., data, orders, result"
                                helperText="If data is nested, specify the parent object name. Leave empty if data is at root level."
                                data-help-title="Parent name"
                                data-help="Specifies the parent object name when the API response nests its data. Leave empty if the data is at the root level."
                            />
                            <TextInput
                                label="Item Code Field"
                                type="text"
                                value={rulesData.rules.itemCodeField || ''}
                                onChange={(e) => setRulesData(prev => ({
                                    ...prev,
                                    rules: { ...prev.rules, itemCodeField: e.target.value }
                                }))}
                                placeholder="e.g., itemCode, productCode, sku"
                                helperText="The field name where the item code is stored"
                                data-help-title="Item code field"
                                data-help="Names the field in the API response where the item code is stored."
                            />
                            <TextInput
                                label="Total Length Field"
                                type="text"
                                value={rulesData.rules.totalLengthField || ''}
                                onChange={(e) => setRulesData(prev => ({
                                    ...prev,
                                    rules: { ...prev.rules, totalLengthField: e.target.value }
                                }))}
                                placeholder="e.g., totalLength, length, quantity"
                                helperText="The field name where the total length is stored"
                                data-help-title="Total length field"
                                data-help="Names the field in the API response where the total length, the value used to calculate points, is stored."
                            />
                        </div>
                    </div>

                    {/* Additional Fields */}
                    <div className="border border-hairline rounded-md p-4">
                        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                            <div>
                                <h4 className="text-title-sm text-ink">Additional Fields</h4>
                                <p className="text-body-md text-body mt-1">Extract additional fields from API response</p>
                            </div>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleAddAdditionalField}
                                leadingIcon={<HiOutlinePlus />}
                                data-help-title="Add field"
                                data-help="Adds an extra field to extract from the API response and store with each submission."
                            >
                                Add Field
                            </Button>
                        </div>

                        {rulesData.rules.additionalFields.length === 0 ? (
                            <p className="text-center py-4 text-body-md text-muted">
                                No additional fields configured. Add fields to extract extra data from API responses.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {rulesData.rules.additionalFields.map((field, index) => (
                                    <div key={index} className="flex items-end gap-3 p-3 border border-hairline rounded-sm">
                                        <div className="flex-1">
                                            <TextInput
                                                label="Field Name"
                                                type="text"
                                                value={field.fieldName}
                                                onChange={(e) => handleAdditionalFieldChange(index, 'fieldName', e.target.value)}
                                                placeholder="e.g., customerName"
                                            />
                                        </div>

                                        <div className="w-32">
                                            <label className="block text-body-md font-medium text-ink mb-1.5">Type</label>
                                            <select
                                                value={field.fieldType}
                                                onChange={(e) => handleAdditionalFieldChange(index, 'fieldType', e.target.value)}
                                                className="w-full h-11 px-3 border border-hairline rounded-sm text-body-md text-ink bg-canvas focus:outline-none focus:border-info-border"
                                            >
                                                <option value="string">String</option>
                                                <option value="number">Number</option>
                                                <option value="boolean">Boolean</option>
                                                <option value="date">Date</option>
                                            </select>
                                        </div>

                                        <div className="flex-1">
                                            <TextInput
                                                label="Default Value"
                                                type="text"
                                                value={field.defaultValue}
                                                onChange={(e) => handleAdditionalFieldChange(index, 'defaultValue', e.target.value)}
                                                placeholder="Optional"
                                            />
                                        </div>

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveAdditionalField(index)}
                                            title="Remove Field"
                                            className="text-signature-coral"
                                        >
                                            <HiOutlineTrash className="text-lg" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Rewards Percentage */}
                    <div className="border border-hairline rounded-md p-4">
                        <h4 className="text-title-sm text-ink mb-4">Rewards Percentage</h4>
                        <TextInput
                            label="Percentage Value (0 to 1)"
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={rulesData.rules.rewardsPercentage || 0}
                            onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                const clampedValue = Math.min(1, Math.max(0, isNaN(value) ? 0 : value));
                                setRulesData(prev => ({
                                    ...prev,
                                    rules: { ...prev.rules, rewardsPercentage: clampedValue }
                                }));
                            }}
                            placeholder="e.g., 0.1 for 10%"
                            helperText="Enter a value between 0 and 1 (e.g., 0.1 = 10%, 0.5 = 50%, 1 = 100%)"
                            data-help-title="Rewards percentage"
                            data-help="Sets what portion of the activity value becomes points, as a number between 0 and 1. For example, 0.1 means 10 percent of the value is awarded as points."
                        />
                    </div>
                </div>
            </Modal>

            {/* Submissions Modal */}
            <Modal
                isOpen={showSubmissionsModal && !!selectedIntegration}
                onClose={() => {
                    if (showApiResponseModal || showPropertiesModal) return;
                    setShowSubmissionsModal(false);
                    setSubmissions([]);
                }}
                title={selectedIntegration ? `Submissions - ${selectedIntegration.name}` : 'Submissions'}
                description="View and manage all integration submissions"
                size="xl"
                footer={
                    <Button variant="secondary" onClick={() => {
                        setShowSubmissionsModal(false);
                        setSubmissions([]);
                    }}>Close</Button>
                }
            >
                {/* Status Filter Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto">
                    {[
                        { key: 'all', label: 'All' },
                        { key: 'succeeded', label: 'Succeeded' },
                        { key: 'failed', label: 'Failed' },
                        { key: 'pending', label: 'Pending' },
                        { key: 'retrying', label: 'Retrying' }
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => handleFilterChange(key)}
                            data-help-title="Status filter"
                            data-help="Filters the submissions list to this processing status and shows how many submissions it contains. Selecting a tab returns to the first page."
                            className={`px-4 py-2 rounded-sm text-body-md font-medium border transition-colors whitespace-nowrap ${
                                statusFilter === key
                                    ? 'bg-ink text-onInk border-ink'
                                    : 'bg-canvas text-body border-hairline hover:bg-surface-soft'
                            }`}
                        >
                            {label} ({statusCounts[key] || 0})
                        </button>
                    ))}
                </div>

                {/* Filter bar */}
                <div className="flex flex-wrap items-end gap-3 mb-4">
                    <div className="flex-1 min-w-[200px]">
                        <TextInput
                            label="Search"
                            type="text"
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                            placeholder="Member or contract number"
                            data-help-title="Search submissions"
                            data-help="Searches submissions by member identification or contract number as you type."
                        />
                    </div>
                    <div>
                        <label className="block text-body-md font-medium text-ink mb-1.5">From</label>
                        <input
                            type="date"
                            value={dateFrom}
                            data-help-title="From date"
                            data-help="Limits the list to submissions made on or after this date."
                            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                            className="h-11 px-3 rounded-sm text-body-md border border-hairline text-ink bg-canvas focus:outline-none focus:border-info-border"
                        />
                    </div>
                    <div>
                        <label className="block text-body-md font-medium text-ink mb-1.5">To</label>
                        <input
                            type="date"
                            value={dateTo}
                            data-help-title="To date"
                            data-help="Limits the list to submissions made on or before this date."
                            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                            className="h-11 px-3 rounded-sm text-body-md border border-hairline text-ink bg-canvas focus:outline-none focus:border-info-border"
                        />
                    </div>
                    <div>
                        <label className="block text-body-md font-medium text-ink mb-1.5">Sort</label>
                        <select
                            value={sortBy}
                            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                            data-help-title="Sort order"
                            data-help="Orders the submissions by newest first, oldest first, or most points awarded."
                            className="h-11 px-3 rounded-sm text-body-md border border-hairline text-ink bg-canvas focus:outline-none focus:border-info-border"
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="points">Most points</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-body-md font-medium text-ink mb-1.5">Per page</label>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                            data-help-title="Page size"
                            data-help="Sets how many submissions are shown per page."
                            className="h-11 px-3 rounded-sm text-body-md border border-hairline text-ink bg-canvas focus:outline-none focus:border-info-border"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    {(searchQuery || dateFrom || dateTo || sortBy !== 'newest') && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                setSearchQuery('');
                                setDateFrom('');
                                setDateTo('');
                                setSortBy('newest');
                                setPage(1);
                            }}
                            data-help-title="Clear filters"
                            data-help="Resets the search, date range, and sort order and returns to the first page."
                        >
                            Clear
                        </Button>
                    )}
                </div>

                {submissionsLoading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ink mx-auto mb-4"></div>
                        <p className="text-body-md text-muted">Loading submissions...</p>
                    </div>
                ) : submissions.length === 0 ? (
                    <div className="text-center py-12">
                        <HiOutlineExclamationCircle className="text-6xl mx-auto mb-4 text-muted" />
                        <h4 className="text-title-sm text-ink mb-2">
                            No {statusFilter !== 'all' ? statusFilter : ''} Submissions
                        </h4>
                        <p className="text-body-md text-body">
                            {statusFilter === 'all'
                                ? 'No submissions have been recorded yet.'
                                : `No ${statusFilter} submissions found.`}
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <p className="text-body-md text-body">
                                {totalSubmissions > 0
                                    ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalSubmissions)} of ${totalSubmissions}`
                                    : 'No submissions match'}
                            </p>
                            <div className="flex gap-2">
                                {submissions.length > 0 && (
                                    <>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => exportSubmissionsCSV(selectedIntegration)}
                                            disabled={exportingSubmissions}
                                            data-help-title="Export CSV"
                                            data-help="Downloads every submission matching the current filters as a CSV file, including status, member, contract number, errors, and the API response."
                                        >
                                            {exportingSubmissions ? 'Exporting...' : 'Export CSV'}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => exportSubmissionsXLSX(selectedIntegration)}
                                            disabled={exportingSubmissions}
                                            data-help-title="Export XLSX"
                                            data-help="Downloads every submission matching the current filters as an Excel file, including status, member, contract number, errors, and the API response."
                                        >
                                            {exportingSubmissions ? 'Exporting...' : 'Export XLSX'}
                                        </Button>
                                    </>
                                )}
                                {isAdmin && (statusFilter === 'failed' || statusFilter === 'all') &&
                                 (statusFilter === 'failed' ? submissions.length > 0 : statusCounts.failed > 0) && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        data-help-title="Retry failed"
                                        data-help="Resends failed submissions through the integration's rules. Useful when the failure was caused by a temporary issue, such as the external system being briefly unreachable, that has since been resolved. Admin only."
                                        onClick={() => handleRetrySubmissions(
                                            statusFilter === 'failed'
                                                ? []
                                                : submissions.filter(s => s.status === 'failed').map(s => s._id)
                                        )}
                                        disabled={retrying}
                                        leadingIcon={!retrying ? <HiOutlineRefresh /> : null}
                                    >
                                        {retrying ? 'Retrying...' : 'Retry Failed'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-hairline">
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Status</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Member</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Contract Number</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Submitted At</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Points Awarded</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Retry Count</th>
                                        <th className="text-left py-3 px-4 text-body-md font-medium text-ink">Error</th>
                                        <th className="text-center py-3 px-4 text-body-md font-medium text-ink">Properties</th>
                                        <th className="text-center py-3 px-4 text-body-md font-medium text-ink">API Response</th>
                                        <th className="text-right py-3 px-4 text-body-md font-medium text-ink">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissions.map((submission, index) => {
                                        const statusVariant = STATUS_BADGE_VARIANTS[submission.status] || 'default';
                                        const retryVariant = submission.retryCount > 2
                                            ? 'danger'
                                            : submission.retryCount > 0
                                            ? 'warning'
                                            : 'default';

                                        return (
                                            <tr
                                                key={submission._id || index}
                                                className="border-b border-hairline hover:bg-surface-soft transition-colors"
                                            >
                                                <td className="py-3 px-4">
                                                    <Badge variant={statusVariant} className="capitalize">
                                                        {submission.status}
                                                    </Badge>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <p className="text-body-md font-medium text-ink">
                                                        {submission.identification}
                                                    </p>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <p className="text-body-md text-body">
                                                        {submission.contractNumber || 'N/A'}
                                                    </p>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <p className="text-body-md text-body">
                                                        {new Date(submission.submittedAt).toLocaleString()}
                                                    </p>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <p className="text-body-md font-medium text-ink">
                                                        {submission.pointsAwarded || 0}
                                                    </p>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <Badge variant={retryVariant}>
                                                        {submission.retryCount}
                                                    </Badge>
                                                </td>
                                                <td className="py-3 px-4">
                                                    {submission.error ? (
                                                        <p className="text-xs text-signature-coral max-w-xs truncate" title={submission.error}>
                                                            {submission.error}
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-muted">-</p>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    {submission.additionalProperties && Object.keys(submission.additionalProperties).length > 0 ? (
                                                        <button
                                                            onClick={() => handleViewProperties(submission.additionalProperties)}
                                                            data-help-title="View properties"
                                                            data-help="Opens a list of the additional properties stored with this submission, with an option to copy them as JSON."
                                                            className="inline-flex items-center px-3 py-1 rounded-sm text-xs font-medium bg-surface-soft text-ink border border-hairline hover:bg-surface-strong transition-colors"
                                                            title="View additional properties"
                                                        >
                                                            <HiOutlineEye className="mr-1" />
                                                            View ({Object.keys(submission.additionalProperties).length})
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    {submission.lastApiResponse ? (
                                                        <button
                                                            onClick={() => handleViewLastApiResponse(submission)}
                                                            data-help-title="API response"
                                                            data-help="Opens the full API request and response recorded for this submission, including any error details. Green means the call succeeded; red means it failed."
                                                            className={`inline-flex items-center px-3 py-1 rounded-sm text-xs font-medium border transition-colors ${
                                                                submission.lastApiResponse.success
                                                                    ? 'bg-signature-mint text-signature-forest border-hairline'
                                                                    : 'bg-signature-coral text-white border-transparent'
                                                            }`}
                                                            title="View API response"
                                                        >
                                                            <HiOutlineCode className="mr-1" />
                                                            {submission.lastApiResponse.success
                                                                ? (submission.lastApiResponse.statusCode || 'OK')
                                                                : (submission.lastApiResponse.errorCode || 'Error')}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {isAdmin && (submission.status === 'failed' || retrySucceededUnlocked) && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleRetrySubmissions([submission._id])}
                                                                disabled={retrying}
                                                                title="Retry submission"
                                                                data-help-title="Retry submission"
                                                                data-help="Resends this submission through the integration's rules. Useful when the failure was caused by a temporary issue that has since been resolved. Admin only."
                                                            >
                                                                <HiOutlineRefresh className="text-lg" />
                                                            </Button>
                                                        )}
                                                        {isAdmin && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDeleteSubmissions([submission._id])}
                                                                title="Delete submission"
                                                                className="text-signature-coral"
                                                                data-help-title="Delete submission"
                                                                data-help="Deletes this submission after confirmation. This cannot be undone. Admin only."
                                                            >
                                                                <HiOutlineTrash className="text-lg" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-1 mt-4 flex-wrap">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-3 py-1 rounded-sm text-body-md border border-hairline text-ink disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-soft"
                                >
                                    ‹ Prev
                                </button>
                                {(() => {
                                    const pages = [];
                                    const win = 1;
                                    const add = (n) => pages.push(n);
                                    const lastIdx = totalPages;
                                    add(1);
                                    const start = Math.max(2, page - win);
                                    const end = Math.min(lastIdx - 1, page + win);
                                    if (start > 2) pages.push('…');
                                    for (let i = start; i <= end; i++) add(i);
                                    if (end < lastIdx - 1) pages.push('…');
                                    if (lastIdx > 1) add(lastIdx);
                                    return pages.map((p, i) =>
                                        p === '…' ? (
                                            <span key={`e${i}`} className="px-2 text-body-md text-muted">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`px-3 py-1 rounded-sm text-body-md border ${
                                                    p === page
                                                        ? 'bg-ink text-onInk border-ink'
                                                        : 'border-hairline text-ink hover:bg-surface-soft'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    );
                                })()}
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="px-3 py-1 rounded-sm text-body-md border border-hairline text-ink disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-soft"
                                >
                                    Next ›
                                </button>
                            </div>
                        )}

                        {submissions.some(sub => sub.additionalProperties && Object.keys(sub.additionalProperties).length > 0) && (
                            <div className="mt-4 p-4 rounded-sm bg-signature-cream">
                                <p className="text-body-md font-medium text-ink">
                                    Tip: {submissions.filter(sub => sub.additionalProperties && Object.keys(sub.additionalProperties).length > 0).length} submission(s) have additional properties. Click "View" to inspect them.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Additional Properties Modal */}
            <Modal
                isOpen={showPropertiesModal && !!selectedProperties}
                onClose={() => {
                    setShowPropertiesModal(false);
                    setSelectedProperties(null);
                }}
                title="Additional Properties"
                description="Custom data submitted with this integration"
                size="md"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(selectedProperties, null, 2));
                                toast.success('Copied to clipboard');
                            }}
                        >
                            Copy JSON
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowPropertiesModal(false);
                                setSelectedProperties(null);
                            }}
                        >
                            Close
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    {selectedProperties && Object.entries(selectedProperties).map(([key, value]) => (
                        <div
                            key={key}
                            className="p-4 rounded-sm border border-hairline bg-surface-soft"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <p className="text-body-md font-semibold mb-2 text-ink">{key}</p>
                                    <div
                                        className="text-body-md font-mono p-3 rounded-sm bg-canvas border border-hairline text-body break-all whitespace-pre-wrap"
                                    >
                                        {typeof value === 'object'
                                            ? JSON.stringify(value, null, 2)
                                            : String(value)}
                                    </div>
                                </div>
                                <Badge variant="default">{typeof value}</Badge>
                            </div>
                        </div>
                    ))}
                </div>

                {selectedProperties && Object.keys(selectedProperties).length === 0 && (
                    <div className="text-center py-8">
                        <p className="text-body-md text-muted">
                            No additional properties found
                        </p>
                    </div>
                )}
            </Modal>

            {/* Last API Response Modal */}
            <Modal
                isOpen={showApiResponseModal}
                onClose={() => {
                    setShowApiResponseModal(false);
                    setApiResponseData(null);
                }}
                title="API Response"
                description={
                    apiResponseData?.identification || apiResponseData?.contractNumber
                        ? `${apiResponseData?.identification ? apiResponseData.identification + ' — ' : ''}${apiResponseData?.contractNumber ? 'Contract: ' + apiResponseData.contractNumber : ''}`
                        : 'Enrichment call for this submission'
                }
                size="lg"
                footer={
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowApiResponseModal(false);
                            setApiResponseData(null);
                        }}
                    >
                        Close
                    </Button>
                }
            >
                {apiResponseLoading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ink mx-auto mb-4"></div>
                        <p className="text-body-md text-muted">Loading...</p>
                    </div>
                ) : apiResponseData?.lastApiResponse ? (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-sm border border-hairline gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                                <Badge variant={apiResponseData.lastApiResponse.success ? 'success' : 'danger'}>
                                    {apiResponseData.lastApiResponse.success ? 'Success' : 'Failed'}
                                </Badge>
                                {apiResponseData.lastApiResponse.statusCode && (
                                    <Badge variant="default">
                                        <span className="font-mono">HTTP {apiResponseData.lastApiResponse.statusCode}</span>
                                    </Badge>
                                )}
                            </div>
                            <p className="text-body-md text-muted">
                                {apiResponseData.lastApiResponse.timestamp
                                    ? new Date(apiResponseData.lastApiResponse.timestamp).toLocaleString()
                                    : 'Unknown'}
                            </p>
                        </div>

                        <div className="p-4 rounded-sm border border-hairline">
                            <h4 className="text-body-md font-medium mb-2 text-ink">Request</h4>
                            <div className="space-y-2">
                                <div>
                                    <span className="text-xs font-medium text-muted">URL:</span>
                                    <p className="text-body-md font-mono mt-1 p-2 rounded-sm bg-surface-soft text-ink break-all">
                                        {apiResponseData.lastApiResponse.url || 'N/A'}
                                    </p>
                                </div>
                                {apiResponseData.lastApiResponse.params && (
                                    <div>
                                        <span className="text-xs font-medium text-muted">Params:</span>
                                        <pre className="text-xs font-mono mt-1 p-2 rounded-sm bg-surface-soft text-ink overflow-auto max-h-20">
                                            {JSON.stringify(apiResponseData.lastApiResponse.params, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!apiResponseData.lastApiResponse.success && apiResponseData.lastApiResponse.error && (
                            <div className="p-4 rounded-sm border border-signature-coral bg-signature-coral/10">
                                <h4 className="text-body-md font-medium mb-2 text-signature-coral">Error</h4>
                                <p className="text-body-md font-mono text-signature-coral">
                                    {apiResponseData.lastApiResponse.error}
                                </p>
                                {apiResponseData.lastApiResponse.errorCode && (
                                    <Badge variant="danger" className="mt-2">
                                        <span className="font-mono">{apiResponseData.lastApiResponse.errorCode}</span>
                                    </Badge>
                                )}
                            </div>
                        )}

                        <div className="p-4 rounded-sm border border-hairline">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <h4 className="text-body-md font-medium text-ink">Response Body</h4>
                                {apiResponseData.lastApiResponse.responseBody && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                JSON.stringify(apiResponseData.lastApiResponse.responseBody, null, 2)
                                            );
                                            toast.success('Response copied to clipboard');
                                        }}
                                    >
                                        Copy JSON
                                    </Button>
                                )}
                            </div>
                            {apiResponseData.lastApiResponse.responseBody ? (
                                <pre className="text-xs font-mono p-3 rounded-sm bg-surface-soft text-ink overflow-auto max-h-96 whitespace-pre-wrap break-words">
                                    {JSON.stringify(apiResponseData.lastApiResponse.responseBody, null, 2)}
                                </pre>
                            ) : (
                                <p className="text-body-md text-muted">No response body available</p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <HiOutlineCode className="text-5xl mx-auto mb-4 text-muted" />
                        <h4 className="text-title-sm text-ink mb-2">No API Response Recorded</h4>
                        <p className="text-body-md text-body">
                            {apiResponseData?.error || 'No calls have been made to the configured URL yet. Submit or test an integration to see the response here.'}
                        </p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Integrations;
